import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  productWorkspaceASIN360Api,
  type AcceptanceCriterion,
  type AIWorkspaceSection,
  type APISpecification,
  type ASIN360PRDStatus,
  type BusinessObjective,
  type DNASection,
  type ExecutiveKPI,
  type KnowledgeGraphRelation,
  type LayoutRegion,
  type NavigationTab,
  type PerformanceTarget,
  type ProductRecommendation,
  type QuickAction,
  type TestingRequirement,
  type TimelineEventType,
  type DatabaseTable,
  type WorkspaceDashboard,
  type WorkspacePhilosophyQuestion,
  type WorkspaceValidation,
} from '../../api/product-workspace-asin360-prd';

type Tab = 'overview' | 'philosophy' | 'layout' | 'tabs' | 'dna' | 'kpis' | 'recommendations' | 'actions' | 'engineering' | 'status';
type Theme = 'light' | 'dark';

export function ProductWorkspaceASIN360PRDPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<WorkspaceDashboard | null>(null);
  const [objectives, setObjectives] = useState<BusinessObjective[]>([]);
  const [philosophy, setPhilosophy] = useState<WorkspacePhilosophyQuestion[]>([]);
  const [layout, setLayout] = useState<LayoutRegion[]>([]);
  const [navTabs, setNavTabs] = useState<NavigationTab[]>([]);
  const [dna, setDna] = useState<DNASection[]>([]);
  const [kpis, setKpis] = useState<ExecutiveKPI[]>([]);
  const [recommendations, setRecommendations] = useState<ProductRecommendation[]>([]);
  const [timeline, setTimeline] = useState<TimelineEventType[]>([]);
  const [graph, setGraph] = useState<KnowledgeGraphRelation[]>([]);
  const [aiSections, setAiSections] = useState<AIWorkspaceSection[]>([]);
  const [actions, setActions] = useState<QuickAction[]>([]);
  const [perf, setPerf] = useState<PerformanceTarget[]>([]);
  const [apis, setApis] = useState<APISpecification[]>([]);
  const [tables, setTables] = useState<DatabaseTable[]>([]);
  const [testing, setTesting] = useState<TestingRequirement[]>([]);
  const [acceptance, setAcceptance] = useState<AcceptanceCriterion[]>([]);
  const [status, setStatus] = useState<ASIN360PRDStatus | null>(null);
  const [validation, setValidation] = useState<WorkspaceValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, obj, phil, lay, tabs, dnaPanel, kpiList, recs, tl, gr, ai, act, pf, api, tbl, tst, acc, st] = await Promise.all([
        productWorkspaceASIN360Api.dashboard(),
        productWorkspaceASIN360Api.businessObjectives(),
        productWorkspaceASIN360Api.workspacePhilosophy(),
        productWorkspaceASIN360Api.layout(),
        productWorkspaceASIN360Api.navigationTabs(),
        productWorkspaceASIN360Api.dnaPanel(),
        productWorkspaceASIN360Api.executiveKPIs(),
        productWorkspaceASIN360Api.recommendations(),
        productWorkspaceASIN360Api.timelineEvents(),
        productWorkspaceASIN360Api.knowledgeGraph(),
        productWorkspaceASIN360Api.aiWorkspace(),
        productWorkspaceASIN360Api.quickActions(),
        productWorkspaceASIN360Api.performanceTargets(),
        productWorkspaceASIN360Api.apis(),
        productWorkspaceASIN360Api.databaseSchema(),
        productWorkspaceASIN360Api.testing(),
        productWorkspaceASIN360Api.acceptanceCriteria(),
        productWorkspaceASIN360Api.status(),
      ]);
      setDashboard(dash);
      setObjectives(obj);
      setPhilosophy(phil);
      setLayout(lay);
      setNavTabs(tabs);
      setDna(dnaPanel);
      setKpis(kpiList);
      setRecommendations(recs);
      setTimeline(tl);
      setGraph(gr);
      setAiSections(ai);
      setActions(act);
      setPerf(pf);
      setApis(api);
      setTables(tbl);
      setTesting(tst);
      setAcceptance(acc);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load ASIN 360 PRD');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await productWorkspaceASIN360Api.validate({
        attrs: {
          layout_complete: true, navigation_complete: true, dna_panel_ready: true,
          recommendations_ready: true, performance_met: true, acceptance_passed: true,
        },
      });
      setValidation(result);
      setActionMsg(result.valid ? 'ASIN 360 PRD passes production readiness gate' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-indigo-900/40 bg-slate-900' : 'border-indigo-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-sky-50/30 text-slate-900';
  const accent = dark ? 'text-sky-300' : 'text-indigo-700';
  const tabActive = 'bg-indigo-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'philosophy', label: 'Philosophy' },
    { id: 'layout', label: 'Layout' },
    { id: 'tabs', label: 'Navigation Tabs' },
    { id: 'dna', label: 'DNA Panel' },
    { id: 'kpis', label: 'Executive KPIs' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'actions', label: 'Actions & AI' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>PRD-000003</p>
          <h1 className="text-2xl font-semibold">Product Workspace (ASIN 360)</h1>
          <p className={`text-sm ${muted}`}>
            Unified product intelligence workspace — Marketplace DNA center, AI workspace, business performance hub and decision console
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-intelligence-v1-prd" className={`${accent} hover:underline`}>PRD-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/daily-growth-command-center-prd" className={`${accent} hover:underline`}>PRD-000002</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Primary Product Operating Environment</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              The ASIN is the primary business entity. Every capability related to a product is accessible from one workspace —
              understand performance, identify opportunities, launch optimizations, and interact with AI without leaving the screen.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Navigation Tabs', value: dashboard.navigation_tabs },
              { label: 'DNA Sections', value: dashboard.dna_sections },
              { label: 'Executive KPIs', value: dashboard.executive_kpis },
              { label: 'Recommendations', value: dashboard.recommendations },
              { label: 'Timeline Events', value: dashboard.timeline_event_types },
              { label: 'Graph Relations', value: dashboard.knowledge_graph_relations },
              { label: 'Quick Actions', value: dashboard.quick_actions },
              { label: 'DB Tables', value: dashboard.database_tables },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Business Objectives</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {objectives.map((o) => (
                <div key={o.key} className="rounded-lg border border-indigo-200 p-3 dark:border-indigo-800">
                  <span className="font-medium">{o.name}</span>
                  <p className={`mt-1 text-xs ${muted}`}>{o.description}</p>
                  {o.kpi && <span className="mt-1 inline-block rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">{o.kpi}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'philosophy' && (
        <div className="space-y-3">
          {philosophy.map((q) => (
            <div key={q.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="text-lg font-semibold">{q.question}</h3>
              {q.answered_by && <p className={`mt-1 text-sm ${muted}`}>Answered by: {q.answered_by}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'layout' && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {layout.sort((a, b) => a.order - b.order).map((r) => (
            <div key={r.key} className={`rounded-xl border p-4 ${card}`}>
              <span className="text-xs text-indigo-600 dark:text-indigo-400">{r.order}</span>
              <h3 className="font-semibold">{r.name}</h3>
              <p className={`text-xs ${muted}`}>{r.region} · {r.component}</p>
              {r.description && <p className={`mt-1 text-sm ${muted}`}>{r.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'tabs' && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {navTabs.sort((a, b) => a.order - b.order).map((t) => (
            <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
              <span className="text-xs text-sky-600 dark:text-sky-400">{t.order}</span>
              <h3 className="font-semibold">{t.name}</h3>
              <code className={`text-xs ${muted}`}>{t.route}</code>
              {t.description && <p className={`mt-1 text-sm ${muted}`}>{t.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'dna' && (
        <div className="grid gap-3 md:grid-cols-2">
          {dna.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{d.name}</h3>
                {d.confidence != null && (
                  <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">{(d.confidence * 100).toFixed(0)}%</span>
                )}
              </div>
              {d.current_state && <p className={`mt-2 text-sm ${muted}`}>State: {d.current_state}</p>}
              {d.benchmark && <p className={`text-xs ${muted}`}>Benchmark: {d.benchmark}</p>}
              {d.ai_insights && d.ai_insights.length > 0 && (
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {d.ai_insights.map((i) => <li key={i}>{i}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'kpis' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {kpis.map((k) => (
            <div key={k.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">{k.name}</h3>
              {k.metric && <p className={`mt-1 text-xs ${muted}`}>{k.metric} ({k.unit})</p>}
              {k.drill_down && <p className={`text-xs ${accent}`}>Drill-down: {k.drill_down}</p>}
              {k.alert_threshold && <p className={`text-xs ${muted}`}>Alert: {k.alert_threshold}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {recommendations.map((r) => (
            <div key={r.key} className={`rounded-xl border p-5 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="font-semibold">{r.title}</h3>
                <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-medium dark:bg-indigo-900">{(r.confidence * 100).toFixed(0)}% confidence</span>
              </div>
              <p className={`mt-2 text-sm ${muted}`}>{r.business_problem}</p>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                <span>Revenue: <strong>{r.expected_revenue}</strong></span>
                <span>Profit: <strong>{r.expected_profit}</strong></span>
                <span>Impact: <strong>{r.expected_improvement}</strong></span>
                <span>Time: {r.implementation_time}</span>
              </div>
              {r.ai_reasoning && <p className={`mt-2 text-xs italic ${muted}`}>{r.ai_reasoning}</p>}
            </div>
          ))}
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-semibold">Timeline Event Types ({timeline.length})</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {timeline.map((e) => (
                <span key={e.key} className="rounded border px-2 py-1 text-xs">{e.name}</span>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-semibold">Knowledge Graph Relations ({graph.length})</h3>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {graph.map((g) => (
                <div key={g.key} className="text-xs">
                  <span className="font-medium">{g.name}</span>
                  <span className={`ml-2 ${muted}`}>{g.source_type} → {g.target_type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'actions' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Quick Actions</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {actions.map((a) => (
                <div key={a.key} className={`flex items-center gap-4 rounded-lg border px-4 py-3 ${card}`}>
                  <span className="font-medium">{a.name}</span>
                  {a.shortcut && <code className="rounded bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">{a.shortcut}</code>}
                  <span className={`ml-auto text-xs ${muted}`}>{a.target_latency}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">AI Workspace</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {aiSections.map((s) => (
                <div key={s.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{s.name}</h3>
                    {s.persistent && <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">Persistent</span>}
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{s.description}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'engineering' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Performance Targets</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {perf.map((p) => (
                <div key={p.key} className={`rounded-lg border px-4 py-3 ${card}`}>
                  <span className="font-medium">{p.surface}</span>
                  <span className="ml-2 text-lg font-semibold text-indigo-600 dark:text-indigo-400">{p.max_value}{p.unit !== 'seconds' && p.unit !== 'ms' ? '' : ` ${p.unit}`}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">APIs</h2>
            {apis.map((a) => (
              <div key={a.key} className={`mb-3 rounded-xl border p-4 ${card}`}>
                <h3 className="font-semibold capitalize">{a.category}: {a.key}</h3>
                <p className={`text-sm ${muted}`}>{a.description}</p>
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {a.endpoints?.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            ))}
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Database Schema</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {tables.map((t) => (
                <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{t.name}</h3>
                  <p className={`text-sm ${muted}`}>{t.purpose}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Testing & Acceptance</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                {testing.map((t) => (
                  <div key={t.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <span className="font-medium capitalize">{t.level}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{t.scope}</span>
                  </div>
                ))}
              </div>
              <div>
                {acceptance.map((a) => (
                  <div key={a.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <p className="text-sm">{a.criterion}</p>
                    {a.target && <p className={`text-xs ${muted}`}>Target: {a.target}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{status.status}</span>
            {status.primary_business_entity && <p className={`mt-3 text-sm ${muted}`}>ASIN is the primary business entity of the platform</p>}
            {status.production_ready && <p className={`text-sm ${muted}`}>Production implementation specification complete</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate ASIN 360 PRD</h3>
            <button type="button" disabled={busy} onClick={validate} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Readiness Validation'}
            </button>
            {validation && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(validation, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
